export interface ActivityPoint {
  date: string;
  total: number;
  successful: number;
  blocked: number;
  user_not_found: number;
  invalid_password: number;
  failed_second_factor: number;
}

export interface IpEvent {
  ip_address: string;
  fraud_score: number;
  blocked_attempts: number;
  total_failures: number;
  successful_authentications: number;
  country_code: string;
  last_activity: number;
  first_seen: number;
  reported_by_others: boolean;
  recommended_action: RecommendedAction;
  priority: Priority;
  metadata: IpMetadata;
  blocked_recent?: number | null;
  failures_recent?: number | null;
  successful_recent?: number | null;
  last_active_date?: string | null;
  activity_days?: number | null;
  failure_breakdown?: FailureBreakdown | null;
  daily_activity?: ActivityPoint[] | null;
}

export interface IpMetadata {
  asn: string;
  as_name: string;
  as_type: string;
  is_proxy: boolean;
  is_vpn: boolean;
  is_tor: boolean;
  is_hosting: boolean;
  is_mobile: boolean;
}

export type RecommendedAction =
  | "ALLOW"
  | "MONITOR_ALERT"
  | "RATE_LIMIT"
  | "CAPTCHA"
  | "BLOCK"
  | "MONITOR_WATCHLIST";

export type Priority = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

export interface DashboardAnalytics {
  total_ips: number;
  total_blocked_attempts: number;
  total_failures: number;
  average_score: number;
  high_risk_ips: number;
  medium_risk_ips: number;
  low_risk_ips: number;
  score_distribution: ScoreBucket[];
  attack_type_distribution: DistributionItem[];
  country_distribution: DistributionItem[];
  as_type_distribution: DistributionItem[];
}

export interface ScoreBucket {
  range: string;
  count: number;
}

export interface DistributionItem {
  name: string;
  count: number;
  percentage: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface CountryCount {
  country_code: string;
  count: number;
}

export interface AsnCount {
  asn: string;
  as_name: string;
  count: number;
}

export interface FilterMetadata {
  total_ips: number;
  country_codes: CountryCount[];
  asns: AsnCount[];
}

export interface DashboardFilters {
  start_date?: string;
  end_date?: string;
  min_score?: number;
  max_score?: number;
  attack_types?: string[];
  country_codes?: string[];
  as_types?: string[];
}

export type SortField = "fraud_score" | "last_activity" | "blocked_recent" | "failures_recent" | "country_code";
export type SortOrder = "asc" | "desc";

export interface EventFilters extends DashboardFilters {
  has_blocked?: "with" | "without";
  has_failures?: "with" | "without";
  has_successful_auth?: "with" | "without";
  has_user_not_found?: "with" | "without";
  has_invalid_password?: "with" | "without";
  has_failed_second_factor?: "with" | "without";
  asns?: string[];
  ip_search?: string;
  sort_by?: SortField;
  sort_order?: SortOrder;
}

// ── Filter panel UI state (controlled component) ─────────────────────
export interface FilterPanelState {
  startDate: Date | null;
  endDate: Date | null;
  minScore: number;
  maxScore: number;
  countryCodes: string[];
  asns: string[];
  ipSearch: string;
  hasBlockedAttempts: boolean | null;
  hasFailures: boolean | null;
  hasSuccessfulAuth: boolean | null;
  hasUserNotFound: boolean | null;
  hasInvalidPassword: boolean | null;
  hasFailedSecondFactor: boolean | null;
}

export const DEFAULT_FILTERS: FilterPanelState = {
  startDate: null,
  endDate: null,
  minScore: 0,
  maxScore: 100,
  countryCodes: [],
  asns: [],
  ipSearch: "",
  hasBlockedAttempts: null,
  hasFailures: null,
  hasSuccessfulAuth: null,
  hasUserNotFound: null,
  hasInvalidPassword: null,
  hasFailedSecondFactor: null,
};

import type { CountryOrigin, TrafficOrigins } from "./trafficOrigins.ts";

/* ── Security Overview (grounded to Shield data) ──────────── */

export interface TelemetrySummary {
  successful: number;
  successful_trend: number;
  blocked: number;
  blocked_trend: number;
  auth_failures: number;
  auth_failures_trend: number;
  failed_second_factor: number;
  failed_second_factor_trend: number;
  /** null when the window had no non-legitimate traffic (ratio undefined). */
  shield_efficiency: number | null;
  shield_efficiency_trend: number;
  shield_efficiency_denominator: number;
  comparison_label: string;
  current_period: string;
  previous_period: string;
}

export interface TimeseriesPoint {
  timestamp: string;
  successful: number;
  blocked: number;
  user_not_found: number;
  invalid_password: number;
  failed_second_factor: number;
  queries?: number;
  would_block?: number;
}


export interface NeverAuthenticated {
  count: number;
  pct: number;
}

export interface AttackerSpendAsn {
  asn: string;
  name: string;
  attempt_count: number;
}

export interface AttackerSpendTier {
  tier: string; // "datacenter" | "residential"
  label: string;
  ip_count: number;
  attempt_count: number;
  asn_count: number;
  est_gb: number;
  price_basis: string;
  price_per_unit_usd: number;
  spend_sek: number;
  representative_asns: AttackerSpendAsn[];
}

export interface AttackerSpendInfrastructure {
  total_devices: number;
  distinct_asns: number;
  datacenter_ips: number;
  residential_devices: number;
  rental_value_sek_per_week: number;
}

export interface AttackerSpendAssumptions {
  bytes_per_attempt: number;
  bytes_per_attempt_low: number;
  bytes_per_attempt_high: number;
  sek_per_usd: number;
  fx_as_of: string;
  mean_attempts_per_ip: number;
  asn_count: number;
  sophistication_position: number;
  prices_as_of: string;
}

export interface AttackerSpend {
  central_sek: number;
  low_sek: number;
  high_sek: number;
  period_days: number;
  tiers: AttackerSpendTier[];
  infrastructure: AttackerSpendInfrastructure;
  assumptions: AttackerSpendAssumptions;
  /** Display-only: IPs active on ≥2 distinct days in the window. Optional — the deployed API may not send it yet. */
  multiday_ip_count?: number;
}

export interface QueriedIp {
  ip_address: string;
  last_risk_overall: number;
  last_queried_at: number;
  query_count: number;
}

export interface QueriedIpsPage {
  items: QueriedIp[];
  continuation: string | null;
}

export interface SecurityOverviewData {
  telemetry: TelemetrySummary;
  traffic_origins: TrafficOrigins;
  /** null when the hostile query failed — its rows are the map's only source. */
  hostile_countries: CountryOrigin[] | null;
  timeseries: TimeseriesPoint[];
  audit_mode: boolean;
  never_authenticated: NeverAuthenticated | null;
  // Snapshot watermark for never_authenticated (ISO-8601), or null when no
  // snapshot exists yet. The count is batch-fresh; the UI labels the tile with this.
  never_authenticated_as_of?: string | null;
  /** False when query usage is too small to headline coverage stats; hides the never-authenticated tile. */
  query_coverage_significant: boolean;
  /** Estimated attacker infrastructure spend for the window, or null when no threat IPs. */
  attacker_spend?: AttackerSpend | null;
}

/* ── Credential Health (grounded to Shield data) ──────────── */

export interface FailureBreakdown {
  user_not_found: number;
  invalid_password: number;
  failed_second_factor: number;
}

/** One hostile source (no successful sign-in in the window) that targeted an account. */
export interface ExposureSource {
  ip_address: string;
  // Invalid passwords + failed second factors by this source on this account in
  // the window; user-not-found is never counted.
  failures: number;
  last_seen?: number | null;
  // Enrichment from the scored snapshot; null when the source has no row there yet.
  fraud_score?: number | null;
  country_code?: string | null;
  asn?: string | null;
  as_name?: string | null;
}

export interface ExposureEntry {
  // The full hash as the customer's collector sent it (SHA-256/512 of the
  // lowercased username), so they can match it against their own logs.
  username_hash: string;
  failures: number;
  // Epoch seconds of the most recent attack on this hash, or null when absent.
  last_seen?: number | null;
  sources: ExposureSource[];
}

/**
 * Stage counts for the credential-defense funnel, hostile sources only. Each
 * stage narrows the previous: `blocked` are stopped by Shield before the
 * credential check; `user_not_found` / `invalid_password` fail at the
 * username/password stage; `failed_second_factor` = valid credentials stopped
 * only by MFA from a source that never signs in successfully, i.e. compromised
 * credentials. Trusted sources are excluded from every stage;
 * `excluded_trusted_attempts` and `successful` say what they contributed.
 */
export interface CredentialFunnel {
  blocked: number;
  user_not_found: number;
  invalid_password: number;
  failed_second_factor: number;
  successful: number;
  excluded_trusted_attempts: number;
}

/**
 * Second-factor outcome split over the window. `failed_hostile` / `failed_trusted`
 * count *sources* (distinct IPs) with a failed second factor — hostile (the IP
 * never authenticates successfully → likely stolen first factor, MFA held) vs
 * trusted (the IP also logs in normally → likely device/MFA problem). Counting
 * sources keeps them consistent with the per-IP IP Activity rows the card links to.
 */
export interface SecondFactorBreakdown {
  failed_hostile: number;
  failed_trusted: number;
}

export interface CredentialHealthData {
  // The one window (days) every card and drill-in link on the page uses.
  window_days: number;
  // Generation of the activity snapshot the funnel and second-factor split were
  // cut from (ISO-8601), or null when the watermark could not be read.
  as_of?: string | null;
  funnel: CredentialFunnel;
  second_factor: SecondFactorBreakdown;
  exposure_index: ExposureEntry[];
  // Snapshot watermark for exposure_index (ISO-8601), or null when no snapshot
  // rows are available.
  exposure_as_of?: string | null;
  // Whether the company sends username hashes: false → never received any (the
  // targeting panel can't populate), true → has data, null/undefined → unknown.
  exposure_available?: boolean | null;
}

/* ── Snapshot Info ─────────────────────────────────────────── */

export interface SnapshotInfo {
  last_updated: string;
  retention_days: number;
  retention_min_date: string;
}

